import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { validateNumber } from 'avilatek-utils';
import { Button, Card, Input, Select } from '@avila-tek/ui/src';
import { FormButtons, FormHeader } from '../../shared/Forms';
import { useNotify } from '../../../hooks';
import { Product, Variant } from '../../../models';
import { UPDATE_VARIANT_INVENTORY } from '../../../graphql/mutation';

interface UpdateVariantInventoryFormProps {
  variant: Variant;
  product: Product;
}

type TMovement = {
  quantity: number;
  movementType: 'in' | 'out';
};

function UpdateVariantInventoryForm({
  variant,
  product,
}: UpdateVariantInventoryFormProps) {
  const notify = useNotify();
  const router = useRouter();

  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState({
    quantity: '',
    movementType: 'in',
  });

  const [updateVariantInventoryMutation] = useMutation<{
    updateVariantInventory: Variant;
  }>(UPDATE_VARIANT_INVENTORY);

  const onSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      if (
        !validateNumber(formState.quantity) ||
        Number(formState.quantity) <= 0
      ) {
        return notify('Ingrese una cantidad válida', 'error');
      }
      if (!formState.movementType) {
        return notify('Tipo de registro invalido', 'error');
      }
      // build object
      const movementData = {
        ...formState,
        quantity: Number(formState.quantity),
        movementType: formState.movementType,
      } as TMovement;

      updateVariant(movementData);
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const updateVariant = async (variantData: TMovement) => {
    const { data } = await updateVariantInventoryMutation({
      variables: {
        data: { ...variantData, variantId: variant._id, __typename: undefined },
      },
    });
    if (data?.updateVariantInventory?._id) {
      notify('Variante actualizada con éxito', 'success');
      router.push(`/app/products/${product._id}?tab=variants`);
    } else {
      notify('Ha ocurrido un error al actualizar la variante', 'error');
    }
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.preventDefault();
    const { value, name } = e.target;
    setFormState({
      ...formState,
      [name]: value,
    });
  };

  return (
    <form>
      <Card>
        <FormHeader
          title={variant?.title}
          href={`/app/products/${product._id}?tab=variants`}
          backBtnText="Productos"
        />
        <Card.Body>
          <div className=" flex md:flex-row flex-col gap-4">
            <div className="w-full">
              <Input
                name="quantity"
                value={formState.quantity}
                label="Cantidad"
                type="number"
                placeholder="Ingrese una cantidad"
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="w-full">
              <Select
                label="Tipo de registro"
                onChange={handleInputChange}
                name="movementType"
                value={formState.movementType}
              >
                <option value="in">Entrada</option>
                <option value="out">Salida</option>
              </Select>
            </div>
          </div>
        </Card.Body>
      </Card>
      <div className="flex justify-end mt-3">
        <div>
          <Button type="submit" onClick={onSubmit} disabled={disabled}>
            Guardar
          </Button>
        </div>
      </div>
    </form>
  );
}

export default UpdateVariantInventoryForm;
